import { useServicePerformance } from '@src/modules/performance/context/reports-api-hooks';
import {
  Link, Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableCaption,
  Center,
  Select,
  Box,
} from '@chakra-ui/react';
import { selectedDatabaseIdState } from '@transport-insights/uikit';
import IndicatorLegend from '@src/modules/performance/components/IndicatorLegend';
import { servicePerformanceLegend } from '@src/modules/performance/shared/constants';
import { useRecoilValue } from 'recoil';
import { useEffect, useMemo, useState } from 'react';
import ReportSection from './ReportSection';
import TrafficLight from '../indicators/TrafficLight';

const informationSource = {
  title: 'Service Performance',
  content: [
    {
      title: 'Non-financial performance measure achievement',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      The annual results published by the Territorial Authority in the Annual Report for the five Roads and Footpaths mandatory non-financial performance measures:
      Road safety, Condition of the sealed road network, Maintenance of a sealed local road network, Condition of footpaths within the local road network, and Response to service requests.
      <br />
      Result colour grading is as follows:
    </Text>
    <IndicatorLegend items={servicePerformanceLegend} />
    <Text fontSize="inherit" mt={2}>
      <em>Target partially achieved</em>
      {' '}
      is used where a TA has broken down the Road Condition performance measure target for urban and rural, and one of the targets have been achieved.
      <br />
      Further reference:
      {' '}
      <Link href="https://www.dia.govt.nz/diawebsite.nsf/Files/NFPM-Guidance-Roads-andFootpaths/$file/NFPM-Guidance-Roads-andFootpaths.doc" isExternal>Roads and Footpaths Guidance (February 2014)</Link>
    </Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Results published in the Territorial Authority Annual Report for the financial year.
    <br />
    Sourced from the Territorial Authority website.
  </Text>,
    },
  ],
};

const resultColourMap = {
  pass: 'GREEN',
  partial: 'ORANGE',
  fail: 'RED',
  na: 'GREY',
  'n/a': 'GREY',
};

const typeOrder = [
  'Condition of the sealed road network',
  'Maintenance of the sealed road network',
  'Road safety',
  'Condition of the footpaths within the local road network',
  'Response to service requests',
];

const resultScore = {
  Pass: 2,
  Partial: 1,
  Fail: 0,
  NA: 0,
};

function ServicePerformanceAnnualTargets() {
  const { data, isLoading } = useServicePerformance();
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const isRegionOrNational = rcaId >= 1000;
  const [selectedType, setSelectedType] = useState('All');

  const latestLockYear = data?.[0]?.LockYear;
  // Need to report on 2 3 year cycles i.e 2018-2021 and 2021-2024 etc
  // Find the current year based on the latest lock year
  const currentYear = parseInt(latestLockYear?.split('/')[0], 10);
  // Find the start year of the first cycle
  // If the current year is a start of a cycle then we need to go back 3 years
  // Otherwise we go back 4 years plus the remainder of the current year
  const startYear = currentYear % 3 === 2 ? currentYear - 3 : currentYear - (currentYear % 3) - 4;
  // Filter the data to only include records with a year greater than the start year
  // Order the data by Type_name
  const filteredByYear = data?.filter((d) => parseInt(d.Year, 10) > startYear)
    .sort((a, b) => typeOrder.indexOf(a.Type_name) - typeOrder.indexOf(b.Type_name));

  const allFinancialYears = filteredByYear?.map((a) => a.FY);
  const distinctFinancialYears = [...new Set(allFinancialYears)].sort();

  // Display the 10 year LTP titles
  const firstCycleStartDate = startYear;
  const secondCycleStartDate = firstCycleStartDate + 3;
  const firstCycleDateRange = `${firstCycleStartDate}-${firstCycleStartDate - 1990}`;
  const secondCycleDateRange = `${secondCycleStartDate}-${secondCycleStartDate - 1990}`;

  const allTypes = filteredByYear?.map((a) => a.Type_name);
  const distinctTypes = useMemo(() => [...new Set(allTypes)], [allTypes]);

  // Weight the financial years
  // Latest year is the highest weight and has a greater multiplier than previous years
  const yearWeight = {};
  distinctFinancialYears.forEach((fy, index) => {
    yearWeight[fy] = distinctFinancialYears.length - 1 === index ? index * 4 : index + 1;
  });

  // Filter the data by selected type
  const filteredData = useMemo(() => {
    if (!filteredByYear) return [];

    if (!isRegionOrNational || selectedType === 'All') {
      return filteredByYear;
    }
    return filteredByYear
      .filter((item) => item.Type_name === selectedType);
  }, [filteredByYear, isRegionOrNational, selectedType]);

  // Group the data by Road Council
  const groupedByRca = filteredData?.reduce((acc, item) => {
    const council = item.RoadCouncil;

    if (!acc[council]) {
      acc[council] = {};
    }

    if (!acc[council][item.Type_name]) {
      acc[council][item.Type_name] = [];
    }

    acc[council][item.Type_name].push(item);
    return acc;
  }, {}) || undefined;

  // Compute weighted results for each Road Council and Type
  // Sort by weighting in descending order
  const sortedRcaData = Object.entries(groupedByRca || {}).map(([rca, types]) => {
    let items;
    let results;

    if (selectedType === 'All') {
      // items = all entries (flattened)
      items = Object.values(types).flat();

      // results = all entries grouped by type
      results = types;
    } else {
      items = types[selectedType];
      if (!items) return null;

      // results = grouped by selectedType only
      results = { [selectedType]: items };
    }

    // Compute weighting from all relevant items
    const weighting = items.reduce((acc, item) => {
      const year = item.FY;
      const weight = yearWeight[year] || 0;
      const result = resultScore[item.Result] ?? 0;
      return acc + result * weight;
    }, 0);

    return { rca, weighting, results };
  }).filter(Boolean).sort((a, b) => b.weighting - a.weighting);

  // Set the initial selected type to the first distinct type if it's a region or national report
  useEffect(() => {
    if (isRegionOrNational && distinctTypes.length > 0) {
      setSelectedType(distinctTypes[0]);
    } else {
      setSelectedType('All');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcaId, data]);

  return (
    <ReportSection
      title="Annual Targets Achieved"
      informationSource={informationSource}
      isLoading={isLoading}
    >
      { sortedRcaData.length > 0 && (
      <Table
        variant="simple"
        sx={{
          'td:nth-of-type(2),td:nth-of-type(5)': {
            borderRight: 'solid 2px lightgray',
          },
        }}
        size="sm"
      >
        <TableCaption>
          <IndicatorLegend items={servicePerformanceLegend} />
        </TableCaption>
        <Thead>
          <Tr>
            <Th colSpan="2" py={2} textAlign="right">
              {isRegionOrNational && (
                <Box className="hidden-print">
                  <Select
                    maxW="fit-content"
                    value={selectedType}
                    size="sm"
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    <option value="All">All</option>
                    {distinctTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </Select>
                </Box>
              )}
            </Th>
            <Th colSpan="3" textAlign="center">
              {firstCycleDateRange}
              {' '}
              Long Term Plan
            </Th>
            <Th colSpan="3" textAlign="center">
              {secondCycleDateRange}
              {' '}
              Long Term Plan
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Th py={2}>&nbsp;</Th>
            <Th textAlign="right" fontWeight="normal">Provision of roads and footpaths</Th>
            {distinctFinancialYears.map((fy) => (
              <Th key={fy} textAlign="center" fontWeight="normal">{fy}</Th>
            ))}
          </Tr>
          {sortedRcaData.map(({ rca, results }) => (
            Object.entries(results).map(([typeName, items], idx, arr) => {
              const resultsByFY = Object.fromEntries(
                items.map((item) => [item.FY, item.Result]),
              );
              return (
                <Tr key={`${rca}-${typeName}`} borderBottom={idx === arr.length - 1 ? '1px solid' : 'none'} borderColor="gray.100">
                  <Td fontWeight="bold" visibility={isRegionOrNational ? 'visible' : 'hidden'} border="none">
                    <Text fontSize="sm">{rca}</Text>
                  </Td>
                  <Td textAlign="right" fontSize="sm" border="none">
                    <Text align="right" fontSize="sm">{typeName}</Text>
                  </Td>
                  {distinctFinancialYears.map((fy) => (
                    <Td key={fy} border="none">
                      <Center>
                        <TrafficLight size={8} colour={resultColourMap[resultsByFY[fy]?.toLowerCase()]} />
                      </Center>
                    </Td>
                  ))}
                </Tr>
              );
            })
          ))}

        </Tbody>
      </Table>
      )}
    </ReportSection>
  );
}

export default ServicePerformanceAnnualTargets;
